import * as projectActions from './project.actions';
import { appStateProject, PROJECT_INITIAL_STATE, ProjectState } from './project.state';
import { cloneDeep } from 'lodash-es';
import { Action, createReducer, on } from '@ngrx/store';
import { Statuses } from '../../shared/models/statuses.enum';
import { ICommentsState } from '../../shared/models/comments-state.interface';

const reducer = createReducer(
  appStateProject.project,
  on(projectActions.updateProjectList, (state, { projectList }) => ({ ...state, list: projectList })),
  on(projectActions.removeProject, (state, { id }) => {
    const index = state.list.findIndex(project => project.id === id);
    return index > -1 ? { ...state, list: state.list.slice(0, index).concat(state.list.slice(index + 1)) } : state;
  }),
  on(projectActions.resetCurrentProject, state => ({
    ...state,
    current: {
      ...state.current,
      project: { ...PROJECT_INITIAL_STATE },
      projectSnapshot: { ...PROJECT_INITIAL_STATE },
      admins: [],
      pendingChanges: false,
      isLocked: false,
      projectMatrixCommentsState: {},
      projectMatrixItems: [],
      statusHistory: [],
    },
  })),
  on(projectActions.populateCurrentProject, (state, { project }) => ({
    ...state,
    current: {
      ...state.current,
      project,
      isLocked: Boolean(project.status && project.status.status !== Statuses.Draft),
    },
  })),
  on(projectActions.populateCurrentProjectSnapshot, (state, { project }) => ({
    ...state,
    current: { ...state.current, projectSnapshot: cloneDeep(project) },
  })),
  on(projectActions.updateProjectInList, (state, { project }) => {
    const updateIndex = state.list.findIndex(d => d.id === project.id);
    return updateIndex > -1
      ? {
          ...state,
          list: [...[...state.list.slice(0, updateIndex)], { ...project }, ...[...state.list.slice(updateIndex + 1)]],
        }
      : state;
  }),
  on(projectActions.pendingCreateProject, (state, { pending }) => ({
    ...state,
    current: { ...state.current, pending },
  })),
  on(projectActions.dispatchedCreateProject, (state, { dispatched }) => ({
    ...state,
    current: { ...state.current, dispatched },
  })),
  on(projectActions.successCreateProject, (state, { success }) => ({
    ...state,
    current: { ...state.current, success },
  })),
  on(projectActions.addProject, (state, { project }) => ({
    ...state,
    list: [...state.list, { ...project }],
  })),
  on(projectActions.updateProjectField, (state, { field, value }) => ({
    ...state,
    current: { ...state.current, project: { ...state.current.project, [field]: value } },
  })),
  on(projectActions.updateProjectFields, (state, { partialProject }) => ({
    ...state,
    current: { ...state.current, project: { ...state.current.project, ...partialProject } },
  })),
  on(projectActions.populateRecordForms, (state, { recordForms }) => ({
    ...state,
    recordForms,
  })),
  on(projectActions.checkCurrentProjectForPendingChanges, (state, { hasChanges }) => ({
    ...state,
    current: {
      ...state.current,
      pendingChanges: hasChanges,
    },
  })),
  on(projectActions.populateStudyAdminList, (state, { admins }) => ({
    ...state,
    current: {
      ...state.current,
      admins,
    },
  })),
  on(projectActions.removeStudyAdminFromList, (state, { id }) => ({
    ...state,
    current: {
      ...state.current,
      admins: state.current.admins.filter(admin => admin.id !== id),
    },
  })),
  on(projectActions.populateProjectMatrixItemsCommentsState, (state, { states }) => {
    const projectMatrixCommentsState = {};
    states.forEach((item: ICommentsState) => {
      projectMatrixCommentsState[item.projectMatrixId] = item;
    });

    return {
      ...state,
      current: {
        ...state.current,
        projectMatrixCommentsState,
      },
    };
  }),
  on(projectActions.populateProjectMatrixItems, (state, { items }) => {
    return {
      ...state,
      current: {
        ...state.current,
        projectMatrixItems: items,
      },
    };
  }),
  on(projectActions.addProjectMatrixItemInList, (state, { item }) => {
    return {
      ...state,
      current: {
        ...state.current,
        projectMatrixItems: [...state.current.projectMatrixItems, item],
      },
    };
  }),
  on(projectActions.updateProjectMatrixItemInList, (state, { item }) => {
    const updateIndex = state.current.projectMatrixItems.findIndex(d => d.id === item.id);
    const list = cloneDeep(state.current.projectMatrixItems);
    if (updateIndex !== -1) {
      list[updateIndex] = item;
    }
    return {
      ...state,
      current: {
        ...state.current,
        projectMatrixItems: list,
      },
    };
  }),
  on(projectActions.deleteProjectMatrixItemInList, (state, { itemId }) => ({
    ...state,
    current: {
      ...state.current,
      projectMatrixItems: state.current.projectMatrixItems.filter(d => d.id !== itemId),
    },
  })),
  on(projectActions.populateProjectStatusHistory, (state, { statuses }) => {
    return {
      ...state,
      current: {
        ...state.current,
        statusHistory: statuses,
      },
    };
  }),
  on(projectActions.populateProjectStatusInList, (state, { status }) => {
    return {
      ...state,
      current: {
        ...state.current,
        statusHistory: [...state.current.statusHistory, status],
      },
    };
  }),
);

export function projectReducer(state: ProjectState, action: Action): ProjectState {
  return reducer(state, action);
}
